/**
 * Komal OS - CRM: bulk invitation emails (product launch / demo / exhibition).
 *
 * Reached from the Customers page after selecting customers (by criteria, or
 * "select all matching"). Four steps: confirm recipients -> describe the event
 * and attach files -> review/edit every AI draft -> download them as .eml files
 * or (CEO only) save them to the Zoho Drafts folder. NOTHING is ever sent from
 * here - the drafts are for you to open and send yourself.
 *
 * The AI writes one template for the whole batch (see bulk_email_draft in
 * app/routes/crm_routes.py); each recipient's draft is that template plus their
 * own local-language greeting and their company name filled in.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  ArrowLeft, Mail, Loader2, CheckCircle2, AlertTriangle, Paperclip, X, Download, Send, Rocket, Wrench,
  Building2, Languages, Sparkles, CheckSquare, Square, ChevronDown, ChevronRight,
} from 'lucide-react';
import {
  bulkEmailRecipients, bulkEmailDraft, bulkEmailDownload, bulkEmailSaveDrafts, listBulkEmailTypes, getBulkEmailStatus,
} from '../../services/crmService';
import { downloadBlob } from '../../utils/downloadFile';
import useAuthStore from '../../store/authStore';
import useUIStore from '../../store/uiStore';

const TYPE_ICONS = { product_launch: Rocket, demo_invitation: Wrench, exhibition_invitation: Building2 };
const EVENT_NAME_HINTS = {
  product_launch: 'e.g. Komal AquaPure X2 launch',
  demo_invitation: 'e.g. Live demo of the AquaPure X2',
  exhibition_invitation: 'e.g. IFAT India 2026 — Hall 3, Stall 12',
};
// Mirror the server's limits (app/crm/mailer.py) so a problem shows up on selection, not after upload.
const MAX_FILES = 10;
const MAX_FILE_BYTES = 10 * 1024 * 1024;
const MAX_TOTAL_BYTES = 20 * 1024 * 1024;
const MAX_PERSONALIZED = 10;
const STEPS = ['Recipients', 'Event & files', 'Review drafts', 'Save or download'];

const fmtSize = (bytes) => (bytes < 1024 * 1024 ? `${Math.max(1, Math.round(bytes / 1024))} KB` : `${(bytes / (1024 * 1024)).toFixed(1)} MB`);
const keyOf = (r) => `${r.account_id}:${r.contact_id}`;
const inputCls = 'w-full border border-gray-300 rounded px-3 py-2 text-sm';

export default function BulkEmailPage() {
  const { state } = useLocation();
  const { user } = useAuthStore();
  const { showToast } = useUIStore();
  const isCeo = user?.role === 'CEO';

  // The Customers page hands the selection over in router state; sessionStorage
  // covers a page refresh, which drops router state.
  const ids = useMemo(() => {
    if (state?.ids?.length) return state.ids;
    try { return JSON.parse(sessionStorage.getItem('crmBulkEmailIds') || '[]'); } catch { return []; }
  }, [state]);

  const [step, setStep] = useState(1);

  const [mode, setMode] = useState('primary'); // primary | all
  const [loadingRecipients, setLoadingRecipients] = useState(ids.length > 0);
  const [recipients, setRecipients] = useState([]);
  const [skipped, setSkipped] = useState([]);
  const [excluded, setExcluded] = useState(() => new Set());
  const [showSkipped, setShowSkipped] = useState(false);

  const [types, setTypes] = useState([]);
  const [typeKey, setTypeKey] = useState('product_launch');
  const [event, setEvent] = useState({ name: '', date: '', venue: '', rsvp_by: '', pitch: '' });
  const [useLocal, setUseLocal] = useState(true);
  const [personalize, setPersonalize] = useState(false);
  const [files, setFiles] = useState([]);
  const [dragging, setDragging] = useState(false);

  const [generating, setGenerating] = useState(false);
  const [drafts, setDrafts] = useState([]);

  const [status, setStatus] = useState(null);
  const [delivering, setDelivering] = useState(null); // 'download' | 'save' | null
  const [saveResult, setSaveResult] = useState(null);

  useEffect(() => {
    listBulkEmailTypes().then((res) => setTypes(res.data.types)).catch(() => {});
    getBulkEmailStatus().then((res) => setStatus(res.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!ids.length) return;
    setLoadingRecipients(true);
    bulkEmailRecipients(ids, mode)
      .then((res) => {
        setRecipients(res.data.recipients);
        setSkipped(res.data.skipped);
        setExcluded(new Set());
      })
      .catch((err) => showToast(err.response?.data?.detail || 'Could not load the recipients', 'error'))
      .finally(() => setLoadingRecipients(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ids, mode]);

  const included = recipients.filter((r) => !excluded.has(keyOf(r)));
  const canPersonalize = included.length > 0 && included.length <= MAX_PERSONALIZED;
  const sendable = drafts.filter((d) => d.include && !d.error);
  const failedDrafts = drafts.filter((d) => d.error);

  const toggleRecipient = (key) => setExcluded((prev) => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });
  const allIncluded = recipients.length > 0 && excluded.size === 0;
  const toggleAllRecipients = () => setExcluded(allIncluded ? new Set(recipients.map(keyOf)) : new Set());

  const addFiles = (list) => {
    const next = [...files];
    for (const f of Array.from(list || [])) {
      if (next.length >= MAX_FILES) { showToast(`At most ${MAX_FILES} attachments`, 'error'); break; }
      if (f.size > MAX_FILE_BYTES) { showToast(`"${f.name}" is over 10 MB`, 'error'); continue; }
      if (next.reduce((sum, x) => sum + x.size, 0) + f.size > MAX_TOTAL_BYTES) { showToast('Attachments can total at most 20 MB', 'error'); break; }
      next.push(f);
    }
    setFiles(next);
  };

  const generate = async () => {
    if (!event.name.trim()) { showToast('Give the event a name first', 'error'); return; }
    setGenerating(true);
    try {
      const res = await bulkEmailDraft({
        recipients: included.map((r) => ({ account_id: r.account_id, contact_id: r.contact_id })),
        email_type: typeKey,
        event,
        use_local_salutation: useLocal,
        personalize: personalize && canPersonalize,
      });
      setDrafts(res.data.drafts.map((d) => ({ ...d, include: !d.error })));
      setSaveResult(null);
      setStep(3);
    } catch (err) {
      showToast(err.response?.data?.detail || 'Could not write the drafts', 'error');
    } finally {
      setGenerating(false);
    }
  };

  const updateDraft = (key, patch) => setDrafts((prev) => prev.map((d) => (d.key === key ? { ...d, ...patch } : d)));

  const deliveryPayload = () => sendable.map((d) => ({
    account_name: d.account_name, contact_name: d.contact_name, contact_email: d.contact_email, subject: d.subject, body: d.body,
  }));

  // A failed blob response has to be read as text to get at the server's message.
  const errorDetail = async (err, fallback) => {
    try { return JSON.parse(await err.response.data.text()).detail || fallback; } catch { return err.response?.data?.detail || fallback; }
  };

  const handleDownload = async () => {
    setDelivering('download');
    try {
      const res = await bulkEmailDownload(deliveryPayload(), files);
      downloadBlob(res.data, `komal-invitations-${new Date().toISOString().slice(0, 10)}.zip`);
      showToast(`Downloaded ${sendable.length} draft${sendable.length === 1 ? '' : 's'} — open them in Zoho Mail`, 'success');
    } catch (err) {
      showToast(await errorDetail(err, 'Could not build the files'), 'error');
    } finally {
      setDelivering(null);
    }
  };

  const handleSave = async () => {
    setDelivering('save');
    try {
      const res = await bulkEmailSaveDrafts(deliveryPayload(), files);
      setSaveResult(res.data);
      showToast(`${res.data.saved} of ${res.data.results.length} saved to ${res.data.mailbox} → Drafts`, res.data.saved === res.data.results.length ? 'success' : 'error');
    } catch (err) {
      showToast(err.response?.data?.detail || 'Could not save to Zoho Drafts', 'error');
    } finally {
      setDelivering(null);
    }
  };

  if (!ids.length) {
    return (
      <div className="min-h-screen p-6">
        <div className="max-w-2xl mx-auto glass-card p-6 text-center">
          <p className="text-sm text-gray-600 mb-3">No customers selected. Pick some on the Customers page first — filter them, tick the rows (or “Select all matching”), then choose Bulk invite.</p>
          <Link to="/crm/accounts" className="text-sm font-semibold text-komal-blue hover:underline">Go to Customers</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-6">
      <div className="max-w-4xl mx-auto">
        <Link to="/crm/accounts" className="flex items-center gap-1.5 text-xs text-white/70 hover:text-white mb-3">
          <ArrowLeft size={13} /> Customers
        </Link>
        <h1 className="text-xl font-bold text-white">Invite customers to an event</h1>
        <p className="text-sm text-white/70 mt-0.5 mb-5">Review every draft before anything leaves — nothing is sent from here.</p>

        <ol className="flex gap-1.5 mb-5">
          {STEPS.map((label, i) => {
            const n = i + 1;
            const active = n === step;
            const done = n < step;
            return (
              <li key={label} className={`flex-1 flex items-center gap-2 rounded-lg border px-3 py-2 ${active ? 'bg-white/20 border-white/40' : 'bg-white/5 border-white/10'}`}>
                <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[11px] font-bold shrink-0 ${done ? 'bg-green-500 text-white' : active ? 'bg-komal-blue text-white' : 'bg-white/15 text-white/70'}`}>
                  {done ? '✓' : n}
                </span>
                <span className={`text-xs font-semibold ${active ? 'text-white' : 'text-white/60'}`}>{label}</span>
              </li>
            );
          })}
        </ol>

        {/* ---------------- Step 1: recipients ---------------- */}
        {step === 1 && (
          <div className="glass-card p-5">
            <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
              <div>
                <h2 className="text-base font-bold text-gray-800">Who is this going to?</h2>
                <p className="text-xs text-gray-500 mt-0.5">
                  {ids.length} customer{ids.length === 1 ? '' : 's'} selected → <span className="font-semibold text-gray-700">{included.length} recipient{included.length === 1 ? '' : 's'}</span>
                  {skipped.length > 0 && <>, {skipped.length} skipped</>}
                </p>
              </div>
              <div className="inline-flex rounded-lg border border-gray-200 overflow-hidden text-xs font-semibold" role="group" aria-label="Which contacts">
                {[['primary', 'One contact per company'], ['all', 'Every contact with an email']].map(([key, label]) => (
                  <button key={key} type="button" aria-pressed={mode === key} onClick={() => setMode(key)}
                    className={`px-3 py-1.5 ${mode === key ? 'bg-komal-blue text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}>
                    {label}
                  </button>
                ))}
              </div>
            </div>

            {loadingRecipients ? (
              <p className="text-sm text-gray-400 py-6 flex items-center gap-2"><Loader2 size={15} className="animate-spin" /> Loading recipients…</p>
            ) : recipients.length === 0 ? (
              <p className="text-sm text-gray-500 py-4">None of the selected customers has a contact with an email address. Add emails on the customer pages, then try again.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-[11px] uppercase tracking-wide text-gray-400 border-b border-gray-100">
                      <th className="pb-2 w-8">
                        <button onClick={toggleAllRecipients} className="text-gray-400 hover:text-komal-blue flex" title={allIncluded ? 'Deselect all' : 'Select all'}>
                          {allIncluded ? <CheckSquare size={15} /> : <Square size={15} />}
                        </button>
                      </th>
                      <th className="pb-2 font-semibold">Company</th>
                      <th className="pb-2 font-semibold">Contact</th>
                      <th className="pb-2 font-semibold">Email</th>
                      <th className="pb-2 font-semibold">Country</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {recipients.map((r) => {
                      const on = !excluded.has(keyOf(r));
                      return (
                        <tr key={keyOf(r)} className={on ? '' : 'opacity-45'}>
                          <td className="py-2">
                            <button onClick={() => toggleRecipient(keyOf(r))} className="flex" aria-label={on ? 'Leave out' : 'Include'}>
                              {on ? <CheckSquare size={15} className="text-komal-blue" /> : <Square size={15} className="text-gray-400" />}
                            </button>
                          </td>
                          <td className="py-2 font-medium text-gray-800">{r.account_name}</td>
                          <td className="py-2 text-gray-600">{r.contact_name}</td>
                          <td className="py-2 text-gray-500">{r.contact_email}</td>
                          <td className="py-2 text-gray-500">{r.country || '—'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {skipped.length > 0 && (
              <div className="mt-4 bg-amber-50 border border-amber-200 rounded-lg px-3.5 py-3 text-xs text-amber-900">
                <button onClick={() => setShowSkipped((v) => !v)} className="flex items-center gap-1.5 font-semibold">
                  {showSkipped ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
                  {skipped.length} customer{skipped.length === 1 ? '' : 's'} left out — no email to send to
                </button>
                {showSkipped && (
                  <ul className="mt-2 space-y-0.5">
                    {skipped.map((s) => <li key={s.account_id}><span className="font-semibold">{s.account_name || `#${s.account_id}`}</span> — {s.reason}</li>)}
                  </ul>
                )}
              </div>
            )}

            <div className="flex justify-end mt-5">
              <button onClick={() => setStep(2)} disabled={included.length === 0}
                className="text-sm font-semibold text-white bg-komal-blue rounded-lg px-4 py-2 disabled:opacity-50">
                Continue with {included.length} →
              </button>
            </div>
          </div>
        )}

        {/* ---------------- Step 2: event + files ---------------- */}
        {step === 2 && (
          <div className="glass-card p-5">
            <h2 className="text-base font-bold text-gray-800">What is the invitation for?</h2>
            <p className="text-xs text-gray-500 mt-0.5 mb-4">One set of details for everyone. Only what you fill in is used — the AI is told never to invent the rest.</p>

            <div className="grid sm:grid-cols-3 gap-2 mb-4">
              {types.map((t) => {
                const Icon = TYPE_ICONS[t.key] || Mail;
                const selected = typeKey === t.key;
                return (
                  <button key={t.key} type="button" onClick={() => setTypeKey(t.key)}
                    className={`text-left border rounded-xl px-3.5 py-3 ${selected ? 'border-komal-blue bg-komal-blue/5' : 'border-gray-200 bg-gray-50 hover:border-gray-300'}`}>
                    <Icon size={16} className={selected ? 'text-komal-blue' : 'text-gray-500'} />
                    <div className={`text-sm font-semibold mt-1 ${selected ? 'text-komal-blue' : 'text-gray-800'}`}>{t.label}</div>
                    <div className="text-[11.5px] text-gray-500 mt-0.5">{t.description}</div>
                  </button>
                );
              })}
            </div>

            <div className="grid sm:grid-cols-2 gap-3 mb-3">
              <label className="block">
                <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">Event name *</span>
                <input value={event.name} onChange={(e) => setEvent({ ...event, name: e.target.value })} placeholder={EVENT_NAME_HINTS[typeKey]} className={`${inputCls} mt-1`} />
              </label>
              <label className="block">
                <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">Date &amp; time</span>
                <input value={event.date} onChange={(e) => setEvent({ ...event, date: e.target.value })} placeholder="e.g. 14 October 2026, 3:00 PM IST" className={`${inputCls} mt-1`} />
              </label>
              <label className="block">
                <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">Venue or link</span>
                <input value={event.venue} onChange={(e) => setEvent({ ...event, venue: e.target.value })} placeholder="Address, or a video-call link" className={`${inputCls} mt-1`} />
              </label>
              <label className="block">
                <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">RSVP by</span>
                <input value={event.rsvp_by} onChange={(e) => setEvent({ ...event, rsvp_by: e.target.value })} placeholder="e.g. 8 October 2026" className={`${inputCls} mt-1`} />
              </label>
              <label className="block sm:col-span-2">
                <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">What should the email highlight? (optional)</span>
                <textarea value={event.pitch} onChange={(e) => setEvent({ ...event, pitch: e.target.value })} rows={2}
                  placeholder="e.g. A compact RO+UF line for pharma-grade water with a smaller footprint" className={`${inputCls} mt-1`} />
              </label>
            </div>

            <div className="mb-3">
              <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">Attachments (optional)</span>
              <label
                htmlFor="bulk-attach"
                onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
                onDragLeave={() => setDragging(false)}
                onDrop={(e) => { e.preventDefault(); setDragging(false); addFiles(e.dataTransfer.files); }}
                className={`mt-1 flex flex-col items-center justify-center border-2 border-dashed rounded-xl py-5 cursor-pointer transition-colors ${dragging ? 'border-komal-blue bg-komal-blue/5' : 'border-gray-300 hover:border-komal-blue'}`}
              >
                <Paperclip size={18} className="text-gray-400 mb-1" />
                <span className="text-sm text-gray-600">Click to attach, or drag files here</span>
                <span className="text-xs text-gray-400 mt-0.5">Brochure, flyer, price list — up to 10 files, 10 MB each, 20 MB in total</span>
              </label>
              <input id="bulk-attach" type="file" multiple className="hidden" onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }} />
              {files.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {files.map((f, i) => (
                    <span key={`${f.name}-${i}`} className="flex items-center gap-2 bg-white border border-gray-200 rounded-lg pl-2.5 pr-1.5 py-1 text-xs">
                      <Paperclip size={12} className="text-gray-400" />
                      <span className="font-semibold text-gray-700 max-w-[180px] truncate">{f.name}</span>
                      <span className="text-gray-400">{fmtSize(f.size)}</span>
                      <button onClick={() => setFiles(files.filter((_, j) => j !== i))} className="text-gray-400 hover:text-red-500" aria-label={`Remove ${f.name}`}><X size={13} /></button>
                    </span>
                  ))}
                </div>
              )}
              <p className="text-[11px] text-gray-400 mt-1.5">The same files go on every draft in this batch.</p>
            </div>

            <label className="flex items-center justify-between gap-3 bg-gray-50 border border-gray-200 rounded-lg px-3.5 py-2.5 mb-2 cursor-pointer">
              <div className="flex items-center gap-2">
                <Languages size={16} className="text-gray-500 shrink-0" />
                <div>
                  <div className="text-xs font-semibold text-gray-800">Local-language greeting</div>
                  <div className="text-[11px] text-gray-500">Opens and closes in the recipient's language when we have one for their country — the body stays in English</div>
                </div>
              </div>
              <input type="checkbox" checked={useLocal} onChange={(e) => setUseLocal(e.target.checked)} className="w-4 h-4 accent-komal-blue shrink-0" />
            </label>
            <label className={`flex items-center justify-between gap-3 border rounded-lg px-3.5 py-2.5 ${canPersonalize ? 'bg-gray-50 border-gray-200 cursor-pointer' : 'bg-gray-50/60 border-gray-100 opacity-60'}`}>
              <div className="flex items-center gap-2">
                <Sparkles size={16} className="text-gray-500 shrink-0" />
                <div>
                  <div className="text-xs font-semibold text-gray-800">Personalize each email</div>
                  <div className="text-[11px] text-gray-500">
                    {canPersonalize
                      ? 'Writes every email separately, using what you quoted them and your notes on the account — slower'
                      : `Only for ${MAX_PERSONALIZED} recipients or fewer (you have ${included.length}) — the rest share one wording, with their company name filled in`}
                  </div>
                </div>
              </div>
              <input type="checkbox" checked={personalize && canPersonalize} disabled={!canPersonalize} onChange={(e) => setPersonalize(e.target.checked)} className="w-4 h-4 accent-komal-blue shrink-0" />
            </label>

            <div className="flex justify-between mt-5">
              <button onClick={() => setStep(1)} className="text-sm font-medium text-gray-500 hover:text-gray-700 px-2">← Back</button>
              <button onClick={generate} disabled={generating || !event.name.trim()}
                className="flex items-center gap-2 text-sm font-semibold text-white bg-komal-blue rounded-lg px-4 py-2 disabled:opacity-50">
                {generating ? <Loader2 size={15} className="animate-spin" /> : <Sparkles size={15} />}
                {generating ? 'Writing drafts…' : `Write ${included.length} draft${included.length === 1 ? '' : 's'}`}
              </button>
            </div>
          </div>
        )}

        {/* ---------------- Step 3: review ---------------- */}
        {step === 3 && (
          <div>
            <div className="glass-card px-5 py-3 mb-3 flex flex-wrap items-center justify-between gap-2">
              <span className="text-sm font-semibold text-gray-800 flex items-center gap-1.5">
                <CheckCircle2 size={15} className="text-green-600" /> {sendable.length} of {drafts.length} included
                {failedDrafts.length > 0 && <span className="text-xs font-normal text-red-600">· {failedDrafts.length} failed</span>}
              </span>
              <span className="text-xs text-gray-400">Edit anything — subject and text — before you continue</span>
            </div>
            <div className="space-y-3">
              {drafts.map((d) => (
                <div key={d.key} className={`glass-card p-4 ${d.include || d.error ? '' : 'opacity-55'}`}>
                  <div className="flex items-center justify-between gap-2 mb-2">
                    <div className="text-sm min-w-0">
                      <span className="font-semibold text-gray-800">{d.account_name || 'Unknown customer'}</span>
                      {d.contact_name && <span className="text-gray-500"> → {d.contact_name}{d.contact_email ? ` (${d.contact_email})` : ''}</span>}
                      {d.language && d.language !== 'English' && (
                        <span className="ml-2 text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded-full bg-purple-50 text-purple-700 border border-purple-200">{d.language} greeting</span>
                      )}
                    </div>
                    {!d.error && (
                      <button onClick={() => updateDraft(d.key, { include: !d.include })}
                        className={`flex items-center gap-1 text-xs font-semibold shrink-0 ${d.include ? 'text-green-700' : 'text-gray-500'}`}>
                        {d.include ? <><CheckSquare size={14} /> Included</> : <><Square size={14} /> Left out</>}
                      </button>
                    )}
                  </div>
                  {d.error ? (
                    <div className="flex items-center gap-1.5 text-xs text-red-600 bg-red-50 border border-red-200 rounded px-2 py-1.5"><AlertTriangle size={12} /> {d.error}</div>
                  ) : (
                    <div className="space-y-1.5">
                      <input value={d.subject} onChange={(e) => updateDraft(d.key, { subject: e.target.value })} className={`${inputCls} font-medium`} aria-label="Subject" />
                      <textarea value={d.body} onChange={(e) => updateDraft(d.key, { body: e.target.value })} rows={9} className={inputCls} aria-label="Email text" />
                    </div>
                  )}
                </div>
              ))}
            </div>
            <div className="flex justify-between mt-5">
              <button onClick={() => setStep(2)} className="text-sm font-medium text-white/80 hover:text-white px-2">← Back to details</button>
              <button onClick={() => setStep(4)} disabled={sendable.length === 0}
                className="text-sm font-semibold text-white bg-komal-blue rounded-lg px-4 py-2 disabled:opacity-50">
                Continue with {sendable.length} →
              </button>
            </div>
          </div>
        )}

        {/* ---------------- Step 4: deliver ---------------- */}
        {step === 4 && (
          <div className="glass-card p-5">
            <h2 className="text-base font-bold text-gray-800">Get the drafts into your mail</h2>
            <p className="text-xs text-gray-500 mt-0.5 mb-4">Nothing is sent by Komal OS. Each draft waits for you to open it and press Send.</p>

            <div className="border border-gray-200 rounded-lg divide-y divide-gray-100 mb-4 text-sm">
              {[
                ['Event', event.name],
                ['Drafts', `${sendable.length} email${sendable.length === 1 ? '' : 's'}`],
                ['Attachments', files.length ? `${files.length} file${files.length === 1 ? '' : 's'} (${fmtSize(files.reduce((s, f) => s + f.size, 0))}) on every draft` : 'None'],
              ].map(([k, v]) => (
                <div key={k} className="flex justify-between px-3.5 py-2"><span className="text-gray-500">{k}</span><span className="font-semibold text-gray-800 text-right">{v}</span></div>
              ))}
            </div>

            <div className="grid sm:grid-cols-2 gap-3">
              <div className="border border-gray-200 rounded-xl p-4">
                <div className="flex items-center gap-1.5 text-sm font-bold text-gray-800 mb-1"><Download size={15} /> Download the drafts</div>
                {isCeo ? (
                  <>
                    <p className="text-xs text-gray-500 mb-3">A zip of .eml files with your attachments. Open them in Zoho Mail (or Outlook) — each opens as an unsent draft.</p>
                    <button onClick={handleDownload} disabled={delivering !== null}
                      className="w-full flex items-center justify-center gap-2 text-sm font-semibold text-white bg-komal-blue rounded-lg px-3 py-2 disabled:opacity-60">
                      {delivering === 'download' ? <Loader2 size={15} className="animate-spin" /> : <Download size={15} />} Download {sendable.length} draft{sendable.length === 1 ? '' : 's'}
                    </button>
                  </>
                ) : (
                  <p className="text-xs text-gray-500">Downloading a file of customer emails is limited to the CEO, so customer data can't leave the system through this screen. Ask the CEO to download and send these, or save them for you.</p>
                )}
              </div>

              <div className="border border-gray-200 rounded-xl p-4">
                <div className="flex items-center gap-1.5 text-sm font-bold text-gray-800 mb-1"><Send size={15} /> Save to Zoho Drafts</div>
                {status?.can_save ? (
                  <>
                    <p className="text-xs text-gray-500 mb-3">Puts each draft straight into the <span className="font-semibold text-gray-700">Drafts</span> folder of <span className="font-semibold text-gray-700">{status.mailbox}</span>.</p>
                    <button onClick={handleSave} disabled={delivering !== null}
                      className="w-full flex items-center justify-center gap-2 text-sm font-semibold text-white bg-green-700 rounded-lg px-3 py-2 disabled:opacity-60">
                      {delivering === 'save' ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />} Save {sendable.length} to Drafts
                    </button>
                  </>
                ) : (
                  <p className="text-xs text-gray-500">
                    {!isCeo
                      ? 'Not connected for you yet — ask the CEO to save these to Drafts, or send them on your behalf.'
                      : 'Not set up yet: it needs the CRM_IMAP_* settings for the mailbox the drafts should land in.'}
                  </p>
                )}
              </div>
            </div>
            {!isCeo && !status?.can_save && (
              <p className="text-[11px] text-gray-400 mt-3">Neither delivery option is available to you directly — this keeps customer contact details from leaving the system through this screen. The CEO can deliver these for you, and their own review is quick since every draft above is already written and approved.</p>
            )}

            {saveResult && (
              <div className="mt-4 border border-green-200 bg-green-50 rounded-lg p-3">
                <div className="text-sm font-semibold text-green-800 flex items-center gap-1.5">
                  <CheckCircle2 size={15} /> {saveResult.saved} of {saveResult.results.length} saved to {saveResult.mailbox} → Drafts — nothing has been sent
                </div>
                <ul className="mt-2 space-y-0.5 text-xs">
                  {saveResult.results.map((r, i) => (
                    <li key={`${r.contact_email}-${i}`} className={r.ok ? 'text-green-800' : 'text-red-700'}>
                      {r.ok ? '✓' : '✕'} {r.account_name || r.contact_email} <span className="opacity-70">({r.contact_email})</span>{!r.ok && ` — ${r.error}`}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="flex justify-between mt-5">
              <button onClick={() => setStep(3)} className="text-sm font-medium text-gray-500 hover:text-gray-700 px-2">← Back to drafts</button>
              <Link to="/crm/accounts" className="text-sm font-semibold text-komal-blue hover:underline px-2 py-2">Done — back to Customers</Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
